import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, SettingsIcon, UndoIcon } from 'lucide-react';
import { ProgressBar } from '../ui/ProgressBar';
import { LogActivityModal } from './LogActivityModal';
import { useActivityPlan } from '../../contexts/ActivityPlanContext';
import { activityById } from '../../data/activityCatalog';
import { activityIconClasses } from '../../utils/activityStyles';
import { actualForPeriod, actualToday, dailyTarget, paceCopy, paceOf } from '../../utils/activityProgress';
import type { ActivityCategory } from '../../types/activity';
export function TodayPlanPanel() {
  const {
    plan,
    activeGoals,
    logs,
    lastLog,
    undoLastLog
  } = useActivityPlan();
  const [modalCategory, setModalCategory] = useState<ActivityCategory | null>(null);
  return <section aria-labelledby="today-plan-heading" className="rounded-2xl border border-navy-100 bg-white shadow-card" data-id="element-175" data-mp="2u4oepj5vulqb">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-navy-100 px-5 py-4" data-id="element-176" data-mp="2p6d7zhavwrcb">
        <div data-id="element-177" data-mp="3vfe7ss0xyvu">
          <h2 id="today-plan-heading" className="text-sm font-semibold text-navy-900" data-id="element-178" data-mp="7ytu8jfyqf9a">
            Today’s priorities
          </h2>
          <p className="mt-0.5 text-xs text-navy-500" data-id="element-179" data-mp="cqbuf5a5wbt7">
            Derived from your perfect week · {plan.workdays} working days
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-180" data-mp="3h4jtdlh0kktq">
          {lastLog ? <button type="button" onClick={undoLastLog} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-navy-200 px-3 text-xs font-semibold text-navy-600 hover:bg-navy-50" data-id="element-181" data-mp="3l53pclpupiea">
              <UndoIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-182" data-mp="3v54amd7a68l4" />
              Undo last
            </button> : null}
          <Link to="/plan" className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-navy-200 px-3 text-xs font-semibold text-navy-700 hover:bg-navy-50" data-id="element-183" data-mp="15glh44fk0pqo">
            <SettingsIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-184" data-mp="fq3q9cm5xx8t" />
            Adjust goals
          </Link>
        </div>
      </header>

      <ul className="divide-y divide-navy-100" data-id="element-185" data-mp="umb9oapqyun1">
        {activeGoals.map(goal => {
        const definition = activityById[goal.category];
        const Icon = definition.icon;
        const isDaily = definition.cadence === 'daily';
        const target = isDaily ? dailyTarget(goal, plan.workdays) : goal.weeklyTarget;
        const actual = isDaily ? actualToday(logs, goal.category) : actualForPeriod(logs, goal.category, 'week');
        const pace = paceOf(actual, target);
        const done = actual >= target;
        return <li key={goal.category} className="px-5 py-4" data-id="element-186" data-mp="3fcjtl3v05acc">
              <div className="flex flex-wrap items-center gap-3" data-id="element-187" data-mp="3aei9cflub4za">
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${activityIconClasses[definition.tone]}`} data-id="element-188" data-mp="3ae8k4u8md1un">
                  <Icon className="h-5 w-5" aria-hidden="true" data-id="element-189" data-mp="1uny75btsf0xz" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-190" data-mp="1fz1rotpeilnc">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-191" data-mp="1fwfi6oiy9w2h">
                    <p className="text-sm font-semibold text-navy-900" data-id="element-192" data-mp="1kxwh4ehvj8gf">
                      {definition.label}
                    </p>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ${paceCopy[pace].classes}`} data-id="element-193" data-mp="2dm0pj7rtxzmt">
                      {done ? 'Done' : paceCopy[pace].label}
                    </span>
                    {definition.logging === 'manual' ? <span className="text-[11px] font-semibold uppercase tracking-wide text-navy-400" data-id="element-194" data-mp="o5j5bnww3rf7">
                        Manual log
                      </span> : null}
                  </div>
                  <p className="mt-0.5 text-xs text-navy-500" data-id="element-195" data-mp="18hx23zrwlcqj">
                    <span className="font-semibold tabular-nums text-navy-700" data-id="element-196" data-mp="13o2kd1vuz7u3">
                      {actual} of {target}
                    </span>{' '}
                    {definition.unit} {isDaily ? 'today' : 'this week'}
                  </p>
                  <ProgressBar className="mt-2" value={actual} max={target} size="sm" tone={done ? 'success' : definition.tone} label={`${definition.label} progress`} data-id="element-197" data-mp="tp3bpjb3fc73" />
                </div>
                <div className="flex shrink-0 items-center gap-2" data-id="element-198" data-mp="1dfghx4491xkl">
                  <button type="button" onClick={() => setModalCategory(goal.category)} className="inline-flex h-11 items-center gap-1.5 rounded-lg bg-navy-800 px-3 text-sm font-semibold text-white hover:bg-navy-900" data-id="element-199" data-mp="1dh9n0ez7s9qh">
                    <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-200" data-mp="2dlaf5nu0vq8g" />
                    Log
                  </button>
                </div>
              </div>
            </li>;
      })}
      </ul>

      <footer className="border-t border-navy-100 px-5 py-3" data-id="element-201" data-mp="2rei7jhpxgpr7">
        <p className="text-xs text-navy-500" data-id="element-202" data-mp="2rg32h8g5mapl">
          Every entry is credited to a realtor, prospect, lead, or past client.
          Change your targets any time from{' '}
          <Link to="/plan" className="font-semibold text-brand-600 hover:underline" data-id="element-203" data-mp="31bmxlsmw95da">
            My Perfect Week
          </Link>
          .
        </p>
      </footer>

      <LogActivityModal open={modalCategory !== null} onClose={() => setModalCategory(null)} defaultCategory={modalCategory ?? 'realtor-outreach'} data-id="element-204" data-mp="3ad4az38x2sya" />
    </section>;
}